import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';
import { useSocket } from '../app/SocketContext';
import { useAppDispatch } from '../app/hooks';
import type { RootState } from '../app/store';
import SOCKET from '../constants/socketTypes';
import { ROUTES } from '../constants/routes';
import { isJudgeDeviceType } from '../constants/deviceType';
import { selectJudge } from '../redux/workSlice';
import { logOutAdjudicator } from '../helpers/adjudicatorLogOut';

// How long the identify overlay flashes unless tapped away.
const IDENTIFY_MS = 6000;

const flash = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

/**
 * Commands sent to this device from marX's Devices screen: sign the judge out
 * ("Log out", "Log out all adjudicators"), or flash the device number so the
 * phone can be found on the judges' table. Mounted once inside the router, so
 * it works on every screen.
 */
const DeviceCommands: React.FC = () => {
  const { t } = useTranslation();
  const socket = useSocket();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const navigate = useNavigate();
  // The number to flash, or null when the overlay is closed (0: none set).
  const [identifyNumber, setIdentifyNumber] = useState<number | null>(null);
  const [loggedOutNotice, setLoggedOutNotice] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleForceLogOut = () => {
      const state = store.getState();
      if (!isJudgeDeviceType(state.settings.deviceType)) return;
      if (!selectJudge(state).uuid) return;
      logOutAdjudicator(socket, dispatch, store.getState);
      setLoggedOutNotice(true);
      navigate(ROUTES.ADJUDICATOR, { replace: true });
    };

    const handleIdentify = () => {
      setIdentifyNumber(store.getState().settings.deviceId || 0);
      try {
        navigator.vibrate?.([400, 200, 400, 200, 400]);
      } catch {
        // No vibration on this device (iPhone): the flashing is enough.
      }
    };

    socket.on(SOCKET.ADJUDICATOR_FORCE_LOG_OUT, handleForceLogOut);
    socket.on(SOCKET.IDENTIFY_DEVICE, handleIdentify);
    return () => {
      socket.off(SOCKET.ADJUDICATOR_FORCE_LOG_OUT, handleForceLogOut);
      socket.off(SOCKET.IDENTIFY_DEVICE, handleIdentify);
    };
  }, [socket, dispatch, store, navigate]);

  useEffect(() => {
    if (identifyNumber === null) return;
    const timer = setTimeout(() => setIdentifyNumber(null), IDENTIFY_MS);
    return () => clearTimeout(timer);
  }, [identifyNumber]);

  return (
    <>
      {identifyNumber !== null && (
        <Box
          onClick={() => setIdentifyNumber(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            cursor: 'pointer',
            animation: `${flash} 0.8s ease-in-out infinite`
          }}
        >
          <Typography variant="h5">{t('device_commands.identify_title')}</Typography>
          <Typography sx={{ fontSize: '35vmin', fontWeight: 'bold', lineHeight: 1.1 }}>
            {identifyNumber || '?'}
          </Typography>
          {!identifyNumber && (
            <Typography variant="body1">{t('device_commands.no_number')}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('device_commands.identify_dismiss')}
          </Typography>
        </Box>
      )}
      <Snackbar
        open={loggedOutNotice}
        autoHideDuration={8000}
        onClose={() => setLoggedOutNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setLoggedOutNotice(false)}
        >
          {t('device_commands.logged_out')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeviceCommands;
